import { useRef, useState } from 'react'

import { Alert } from './ui/Alert'
import { Button } from './ui/Button'
import { ImageCropDialog } from './ui/ImageCropDialog'
import { UploadProgressBar } from './ui/UploadProgressBar'
import { uploadProductImages } from '../services/uploadService'
import * as storeApi from '../services/storeService'
import { getUserFacingErrorMessage } from '../utils/userFacingError'

/** Same limits the API enforces; checked here first so the merchant gets instant feedback. */
export const FAVICON_MIN_SIDE = 128
export const FAVICON_RECOMMENDED_SIDE = 512

type Props = {
  storeId: number | null
  /** Current icon URL ('' when the store uses the default icon). */
  value: string
  /** Called with the new URL ('' after removing) once the store has been saved. */
  onSaved: (url: string) => void | Promise<void>
}

function readImageSize(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight })
    img.onerror = () => reject(new Error('We could not read that image. Try exporting it again as a PNG.'))
    img.src = url
  })
}

/**
 * Per-store browser-tab icon. Strict on purpose: the icon is shown at 16-32px, so it must be a square symbol.
 * Merchants crop to a square here; the API re-checks everything and explains exactly what to change.
 */
export function FaviconSettings({ storeId, value, onSaved }: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [crop, setCrop] = useState<{ url: string; mime: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  function closeCrop() {
    if (crop?.url) URL.revokeObjectURL(crop.url)
    setCrop(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  async function handleFile(ev: React.ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0]
    if (!file || storeId == null) return
    setError(null)
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setError('Use a PNG, JPG or WEBP image for the tab icon.')
      ev.target.value = ''
      return
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be 5MB or smaller.')
      ev.target.value = ''
      return
    }
    const url = URL.createObjectURL(file)
    try {
      const { width, height } = await readImageSize(url)
      if (Math.min(width, height) < FAVICON_MIN_SIDE) {
        URL.revokeObjectURL(url)
        setError(
          `Your icon is ${width}×${height} pixels. It must be at least ${FAVICON_MIN_SIDE}×${FAVICON_MIN_SIDE} (${FAVICON_RECOMMENDED_SIDE}×${FAVICON_RECOMMENDED_SIDE} recommended) so it stays sharp in browser tabs.`,
        )
        ev.target.value = ''
        return
      }
    } catch (e) {
      URL.revokeObjectURL(url)
      setError(e instanceof Error ? e.message : 'We could not read that image.')
      ev.target.value = ''
      return
    }
    setCrop({ url, mime: file.type })
  }

  async function upload(file: File) {
    if (storeId == null) return
    closeCrop()
    setBusy(true)
    setProgress(0)
    setError(null)
    try {
      const urls = await uploadProductImages([file], setProgress, { purpose: 'favicon' })
      const url = urls[0]
      if (!url) throw new Error('Upload did not return a URL')
      await storeApi.updateStore(storeId, { faviconUrl: url })
      await onSaved(url)
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not save the tab icon'))
    } finally {
      setBusy(false)
      setProgress(0)
    }
  }

  async function remove() {
    if (storeId == null) return
    setBusy(true)
    setError(null)
    try {
      await storeApi.updateStore(storeId, { faviconUrl: '' })
      await onSaved('')
    } catch (e) {
      setError(getUserFacingErrorMessage(e, 'Could not remove the tab icon'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-1.5">
      <span className="text-xs font-medium text-muted-foreground">Browser tab icon (favicon)</span>
      <p className="text-xs text-muted-foreground">
        The small icon in your shop’s browser tab and bookmarks. Use a simple <strong className="text-foreground">square</strong>{' '}
        symbol, at least {FAVICON_MIN_SIDE}×{FAVICON_MIN_SIDE} pixels ({FAVICON_RECOMMENDED_SIDE}×{FAVICON_RECOMMENDED_SIDE} is
        best), cropped so it fills the square. A wide logo with text can’t be read at tab size. Until you add one, your shop
        shows the Kartly icon.
      </p>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => void handleFile(e)}
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="button"
          variant="secondary"
          disabled={busy || storeId == null || crop != null}
          onClick={() => inputRef.current?.click()}
        >
          {busy ? 'Saving…' : value ? 'Replace icon' : 'Upload icon'}
        </Button>
        {value ? (
          <Button type="button" variant="ghost" disabled={busy} onClick={() => void remove()}>
            Remove
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">No icon yet — using the Kartly icon</span>
        )}
      </div>

      {value ? (
        <div className="mt-2 flex items-center gap-4 rounded-xl border border-border bg-background/50 p-3" data-testid="favicon-preview">
          <div className="flex items-center gap-2 rounded-t-lg border border-border bg-card px-3 py-1.5 text-xs text-foreground shadow-sm">
            <img src={value} alt="" width={16} height={16} className="h-4 w-4" />
            <span>Your shop</span>
          </div>
          <img src={value} alt="Tab icon at 32 pixels" width={32} height={32} className="h-8 w-8" />
          <img src={value} alt="Tab icon at 64 pixels" width={64} height={64} className="h-16 w-16 rounded-lg" />
        </div>
      ) : null}

      {busy && progress > 0 ? <UploadProgressBar active label="Uploading…" progress={progress} /> : null}
      {error ? (
        <div className="mt-2">
          <Alert variant="destructive" title="This icon can’t be used">
            {error}
          </Alert>
        </div>
      ) : null}

      <ImageCropDialog
        open={crop != null}
        imageUrl={crop?.url ?? null}
        sourceMimeType={crop?.mime}
        aspect={1}
        preferPng
        trimMargins={false}
        maxEdge={FAVICON_RECOMMENDED_SIDE}
        fileName="tab-icon"
        title="Crop tab icon"
        description="Make the symbol fill the square. It is shown at only 16–32 pixels, so keep it simple and large."
        onCancel={closeCrop}
        onConfirm={(file) => void upload(file)}
      />
    </div>
  )
}
